import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { MAT_DIALOG_DATA } from '@angular/material/dialog'
import { ProfilesEntity } from '@galabid/gb-dashboard/user/data-access'
import { PaymentMethodUtils, StringUtils } from '@galabid/shared/utils/helpers'
import { WalletTypeEnum } from 'libs/shared/models/customer-payment-methods/customer-payment-methods.models'
import {
  CardEntity,
  CustomerPaymentMethodsEntity,
  CustomerPaymentMethodsFacade,
} from 'gb-dashboard/checkout/data-access-customer-payment-methods'
import * as CustomerPaymentMethodsActions from 'libs/gb-dashboard/checkout/data-access-customer-payment-methods/src/lib/+state/customer-payment-methods.actions'
import { EventTicketProfileEntity } from 'libs/shared/event-tickets/data-access/src/lib/+state/base-event-tickets.models'
import { filter, map, of, take } from 'rxjs'
import { GbStripeService } from 'libs/gb-dashboard/shared/data-access-stripe/src/lib/gb-stripe.service'
import * as StripeActions from 'libs/gb-dashboard/shared/data-access-stripe/src/lib/+state/stripe.actions'
import { StripeFacade } from 'gb-dashboard/shared/data-access-stripe'

@Component({
  selector: 'gb-customer-payment-methods-dialog',
  standalone: false,
  templateUrl: './gb-customer-payment-methods-dialog.component.html',
  styleUrl: './gb-customer-payment-methods-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbCustomerPaymentMethodsDialogComponent implements OnInit, OnDestroy {
  profile: EventTicketProfileEntity | ProfilesEntity

  get stripeCustomerUuid() {
    return this.profile?.stripe_customer
  }

  paymentMethods$ = this.customerPaymentMethodsFacade.allCustomerPaymentMethods$

  emptyState$ = this.paymentMethods$.pipe(map((cards) => !cards.length))

  addCard = false

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { profile: EventTicketProfileEntity | ProfilesEntity },
    private customerPaymentMethodsFacade: CustomerPaymentMethodsFacade,
    private gbStripeService: GbStripeService,
    private stripeFacade: StripeFacade,
  ) {
    this.profile = data.profile
  }

  ngOnInit(): void {
    this.loadCustomerPaymentMethods()
  }

  loadCustomerPaymentMethods(): void {
    if (!this.stripeCustomerUuid) {
      return
    }
    this.customerPaymentMethodsFacade.dispatch(
      CustomerPaymentMethodsActions.loadCustomerPaymentMethods({
        stripeCustomerUuid: this.stripeCustomerUuid,
      }),
    )
  }

  ngOnDestroy(): void {
    this.customerPaymentMethodsFacade.dispatch(CustomerPaymentMethodsActions.clearCustomerPaymentMethods())
    this.gbStripeService.resetStripeElements()
  }

  getCardLabel(cPaymentMethod: CustomerPaymentMethodsEntity): string[] {
    return [
      StringUtils.toProperCase(cPaymentMethod.card.brand),
      '●●●● ' + cPaymentMethod.card.last4,
      `Exp: ${cPaymentMethod.card.exp_month}/${cPaymentMethod.card.exp_year}`,
    ]
  }

  displayName(): boolean {
    return !!(this.profile.first_name || this.profile.first_name)
  }

  getProfileName(): { profileName: string } {
    return { profileName: `${this.profile.first_name + ' ' + this.profile.last_name}` }
  }

  getCardBrand(card: CardEntity): string {
    if (card.wallet_type === WalletTypeEnum.ApplePay || card.wallet_type === WalletTypeEnum.GooglePay) {
      return card.wallet_type
    }

    return card.brand
  }

  isCardExpired(paymentMethod: CustomerPaymentMethodsEntity): boolean {
    return PaymentMethodUtils.isCardExpired(paymentMethod)
  }

  startAddCard() {
    this.addCard = true
  }
  cancelAddCard() {
    this.addCard = false
  }

  saveCard() {
    this.gbStripeService.paymentElementReady$.pipe(filter(Boolean), take(1)).subscribe(() => {
      if (!this.profile.stripe_customer || !this.profile.user) return
      this.stripeFacade.dispatch(
        StripeActions.addNewCard({
          user: this.profile.user,
          stripeCustomer: this.profile.stripe_customer,
          onSuccess: () => {
            this.cancelAddCard()
            this.loadCustomerPaymentMethods()
          },
        }),
      )
    })
  }
}
